import React, { useState, useEffect } from 'react';

const FocusModePage = () => {
  const [isActive, setIsActive] = useState(false);
  const [duration, setDuration] = useState(25);
  const [timeLeft, setTimeLeft] = useState(duration * 60);
  const [blockedApps, setBlockedApps] = useState([
    { id: 1, name: 'Social Media', isBlocked: true },
    { id: 2, name: 'Entertainment', isBlocked: true },
    { id: 3, name: 'News', isBlocked: false },
    { id: 4, name: 'Email', isBlocked: false },
    { id: 5, name: 'Messaging', isBlocked: true },
  ]);
  
  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Handle timer
  useEffect(() => {
    let interval = null;
    
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((timeLeft) => timeLeft - 1);
      }, 1000);
    } else if (isActive && timeLeft === 0) {
      setIsActive(false);
      // Play sound or notification when timer ends
      const audio = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-alarm-digital-clock-beep-989.mp3');
      audio.play();
    } else {
      clearInterval(interval);
    }
    
    return () => clearInterval(interval);
  }, [isActive, timeLeft]);
  
  // Start or pause timer
  const toggleTimer = () => {
    if (!isActive) {
      setTimeLeft(duration * 60);
    }
    setIsActive(!isActive);
  };
  
  // Reset timer
  const resetTimer = () => {
    setIsActive(false);
    setTimeLeft(duration * 60);
  };
  
  // Toggle app blocking
  const toggleAppBlocking = (id) => {
    setBlockedApps(
      blockedApps.map((app) =>
        app.id === id ? { ...app, isBlocked: !app.isBlocked } : app
      )
    );
  };
  
  // Calculate progress percentage
  const progressPercentage = 100 - (timeLeft / (duration * 60)) * 100;
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-gray-900">Focus Mode</h1>
          <p className="mt-1 text-lg text-gray-500">
            Block distractions and stay focused
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div className="card">
          <h2 className="text-xl font-medium text-gray-900 mb-6">Focus Timer</h2>
          
          <div className="flex flex-col items-center">
            <div className="relative w-64 h-64">
              {/* Circular progress */}
              <svg className="w-full h-full" viewBox="0 0 100 100">
                {/* Background circle */}
                <circle
                  className="text-gray-200"
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="8"
                />
                {/* Progress circle */}
                <circle
                  className={`${isActive ? 'text-primary' : 'text-gray-400'}`}
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="8"
                  strokeDasharray="283"
                  strokeDashoffset={283 - (283 * progressPercentage) / 100}
                  strokeLinecap="round"
                  transform="rotate(-90 50 50)"
                />
              </svg>
              {/* Timer display */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-5xl font-semibold text-gray-900">{formatTime(timeLeft)}</div>
                  <div className="mt-2 text-sm text-gray-500">
                    {isActive ? 'Focus time remaining' : 'Ready to focus'}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 flex space-x-4">
              <button
                onClick={toggleTimer}
                className={`btn ${isActive ? 'btn-secondary' : 'btn-primary'} px-6`}
              >
                {isActive ? 'Pause' : 'Start Focus'}
              </button>
              <button onClick={resetTimer} className="btn btn-outline">
                Reset
              </button>
            </div>
            
            <div className="mt-8 w-full">
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                Focus Duration: {duration} minutes
              </label>
              <input
                type="range"
                id="duration"
                min="5"
                max="60"
                step="5"
                value={duration}
                onChange={(e) => {
                  setDuration(parseInt(e.target.value));
                  setTimeLeft(parseInt(e.target.value) * 60);
                }}
                disabled={isActive}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>5m</span>
                <span>15m</span>
                <span>25m</span>
                <span>40m</span>
                <span>60m</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card">
          <h2 className="text-xl font-medium text-gray-900 mb-6">Block Distractions</h2>
          
          <div className="space-y-4">
            <p className="text-gray-500">
              Select which app categories to block during your focus session:
            </p>
            
            {blockedApps.map((app) => (
              <div key={app.id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-md bg-gray-100 flex items-center justify-center text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="ml-3 text-gray-900">{app.name}</span>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={app.isBlocked}
                    onChange={() => toggleAppBlocking(app.id)}
                  />
                  <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${app.isBlocked ? 'bg-primary' : 'bg-gray-200'}`}>
                    <div className={`absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform duration-200 ease-in-out ${app.isBlocked ? 'transform translate-x-5' : ''}`}></div>
                  </div>
                </label>
              </div>
            ))}
            
            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Focus Tips</h3>
              <ul className="text-gray-500 space-y-2 list-disc pl-5">
                <li>Find a quiet, comfortable space to work</li>
                <li>Close unnecessary browser tabs</li>
                <li>Put your phone on silent or in another room</li>
                <li>Use the Pomodoro technique: 25 minutes of focus, then a 5-minute break</li>
                <li>Stay hydrated and take short breaks to stretch</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-8 card">
        <h2 className="text-xl font-medium text-gray-900 mb-4">Focus History</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Today, 10:30 AM</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">25 minutes</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Completed
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Today, 9:00 AM</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">15 minutes</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Completed
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Yesterday, 3:45 PM</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">40 minutes</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                    Interrupted
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Yesterday, 11:15 AM</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">25 minutes</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Completed
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FocusModePage; 